package com.zq.webproject.ui.recharge

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Close
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import com.zq.webproject.ui.order.QrcodeImageView
import com.zq.webproject.utils.TokenManager
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.Font
import webproject.composeapp.generated.resources.Res
import webproject.composeapp.generated.resources.font_song

// 统一字体
private val CaiFont: FontFamily
    @Composable
    get() = FontFamily(Font(Res.font.font_song))

/**
 * 充值界面
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun RechargeScreen(
    onBackClick: () -> Unit
) {
    var rechargeAmount by remember { mutableStateOf("") }
    var selectedAmount by remember { mutableStateOf<Int?>(null) }
    
    // 创建 ViewModel 实例
    val viewModel = remember { RechargeViewModel() }
    
    // 支付宝二维码弹框状态
    var showAlipayDialog by remember { mutableStateOf(false) }
    var alipayQrcodeUrl by remember { mutableStateOf<String?>(null) }
    var alipayOrderNo by remember { mutableStateOf<String?>(null) }
    
    // 预设金额选项
    val presetAmounts = listOf(1, 5, 10, 50, 100, 500)
    
    Scaffold(
        topBar = {
            TopAppBar(
                title = {
                    Text(
                        text = "账户充值",
                        fontFamily = CaiFont,
                        fontWeight = FontWeight.Bold
                    )
                },
                navigationIcon = {
                    IconButton(onClick = {
                        println("RechargeScreen - 点击返回按钮")
                        onBackClick()
                    }) {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = "返回"
                        )
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = Color(0xFF1E88E5),
                    titleContentColor = Color.White,
                    navigationIconContentColor = Color.White
                )
            )
        }
    ) { paddingValues ->
        val scrollState = rememberScrollState()
        
        Column(
            modifier = Modifier
                .fillMaxSize()
                .background(Color(0xFFF5F5F5))
                .padding(paddingValues)
                .imePadding()  // 键盘弹出时自动添加内边距
                .verticalScroll(scrollState)
                .padding(16.dp)
        ) {
            // 显示错误消息
            viewModel.errorMessage?.let { error ->
                Card(
                    modifier = Modifier.fillMaxWidth(),
                    colors = CardDefaults.cardColors(containerColor = Color(0xFFFFEBEE)),
                    shape = RoundedCornerShape(8.dp)
                ) {
                    Text(
                        text = error,
                        color = Color(0xFFD32F2F),
                        fontSize = 14.sp,
                        modifier = Modifier.padding(12.dp),
                        fontFamily = CaiFont
                    )
                }
                Spacer(modifier = Modifier.height(12.dp))
            }

            // 显示成功消息
            viewModel.successMessage?.let { success ->
                Card(
                    modifier = Modifier.fillMaxWidth(),
                    colors = CardDefaults.cardColors(containerColor = Color(0xFFE8F5E9)),
                    shape = RoundedCornerShape(8.dp)
                ) {
                    Text(
                        text = success,
                        color = Color(0xFF2E7D32),
                        fontSize = 14.sp,
                        modifier = Modifier.padding(12.dp),
                        fontFamily = CaiFont
                    )
                }
                Spacer(modifier = Modifier.height(12.dp))
            }
            
            // 充值金额卡片
            Card(
                modifier = Modifier.fillMaxWidth(),
                shape = RoundedCornerShape(12.dp),
                colors = CardDefaults.cardColors(containerColor = Color.White),
                elevation = CardDefaults.cardElevation(2.dp)
            ) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(20.dp)
                ) {
                    Text(
                        text = "充值金额",
                        fontSize = 16.sp,
                        fontWeight = FontWeight.Bold,
                        fontFamily = CaiFont
                    )
                    
                    Spacer(modifier = Modifier.height(16.dp))
                    
                    // 自定义金额输入
                    OutlinedTextField(
                        value = rechargeAmount,
                        onValueChange = { value ->
                            // 只允许输入数字和小数点
                            if (value.isEmpty() || value.matches(Regex("^\\d*\\.?\\d*$"))) {
                                rechargeAmount = value
                                selectedAmount = null // 清除预设选择
                            }
                        },
                        label = {
                            Text(
                                text = "请输入充值金额",
                                fontFamily = CaiFont
                            )
                        },
                        placeholder = {
                            Text(
                                text = "最低充值0.1元",
                                fontFamily = CaiFont
                            )
                        },
                        leadingIcon = {
                            Text(
                                text = "￥",
                                fontSize = 20.sp,
                                fontWeight = FontWeight.Bold,
                                color = Color(0xFFFF6F00)
                            )
                        },
                        modifier = Modifier.fillMaxWidth(),
                        singleLine = true,
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedBorderColor = Color(0xFF1E88E5),
                            focusedLabelColor = Color(0xFF1E88E5)
                        )
                    )
                    
                    Spacer(modifier = Modifier.height(20.dp))
                    
                    Text(
                        text = "快速选择",
                        fontSize = 14.sp,
                        color = Color.Gray,
                        fontFamily = CaiFont
                    )
                    
                    Spacer(modifier = Modifier.height(12.dp))
                    
                    // 预设金额按钮
                    Column(
                        modifier = Modifier.fillMaxWidth(),
                        verticalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        presetAmounts.chunked(3).forEach { rowAmounts ->
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.spacedBy(12.dp)
                            ) {
                                rowAmounts.forEach { amount ->
                                    val isSelected = selectedAmount == amount
                                    OutlinedButton(
                                        onClick = {
                                            selectedAmount = amount
                                            rechargeAmount = amount.toString()
                                        },
                                        modifier = Modifier
                                            .weight(1f)
                                            .height(48.dp),
                                        colors = ButtonDefaults.outlinedButtonColors(
                                            containerColor = if (isSelected) Color(0xFFE3F2FD) else Color.Transparent,
                                            contentColor = if (isSelected) Color(0xFF1E88E5) else Color.Gray
                                        ),
                                        border = ButtonDefaults.outlinedButtonBorder.copy(
                                            width = if (isSelected) 2.dp else 1.dp,
                                            brush = androidx.compose.ui.graphics.SolidColor(
                                                if (isSelected) Color(0xFF1E88E5) else Color(0xFFE0E0E0)
                                            )
                                        ),
                                        shape = RoundedCornerShape(8.dp)
                                    ) {
                                        Text(
                                            text = "￥$amount",
                                            fontSize = 16.sp,
                                            fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Normal,
                                            fontFamily = CaiFont
                                        )
                                    }
                                }
                                // 如果这一行不足3个，添加空白占位
                                repeat(3 - rowAmounts.size) {
                                    Spacer(modifier = Modifier.weight(1f))
                                }
                            }
                        }
                    }
                }
            }
            
            Spacer(modifier = Modifier.height(24.dp))
            
            // 支付方式说明
            Card(
                modifier = Modifier.fillMaxWidth(),
                shape = RoundedCornerShape(12.dp),
                colors = CardDefaults.cardColors(containerColor = Color(0xFFFFF3E0)),
                elevation = CardDefaults.cardElevation(0.dp)
            ) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(16.dp)
                ) {
                    Text(
                        text = "💡 充值说明",
                        fontSize = 14.sp,
                        fontWeight = FontWeight.Bold,
                        color = Color(0xFFFF6F00),
                        fontFamily = CaiFont
                    )
                    Spacer(modifier = Modifier.height(8.dp))
                    Text(
                        text = "• 支持支付宝扫码支付\n• 充值金额实时到账\n• 最低充值金额为0.1元\n• 充值后可用于购买商品或升级",
                        fontSize = 13.sp,
                        color = Color(0xFF666666),
                        lineHeight = 20.sp,
                        fontFamily = CaiFont
                    )
                }
            }
            
            Spacer(modifier = Modifier.height(24.dp))
            
            // 充值按钮
            Button(
                onClick = {
                    val amount = rechargeAmount.toDoubleOrNull()
                    if (amount == null || amount < 0.1) {
                        println("RechargeScreen - 充值金额无效或小于0.1元")
                    } else {
                        println("RechargeScreen - 发起充值：￥$amount")
                        viewModel.recharge(
                            amount = amount,
                            onAlipayDialogShow = { qrcodeUrl, orderNo ->
                                println("RechargeScreen - 显示支付宝二维码弹框")
                                alipayQrcodeUrl = qrcodeUrl
                                alipayOrderNo = orderNo
                                showAlipayDialog = true
                            }
                        )
                    }
                },
                modifier = Modifier
                    .fillMaxWidth()
                    .height(52.dp),
                enabled = !viewModel.isLoading && (rechargeAmount.toDoubleOrNull()?.let { it >= 0.1 } ?: false),
                colors = ButtonDefaults.buttonColors(
                    containerColor = Color(0xFF4CAF50),
                    disabledContainerColor = Color(0xFFBDBDBD)
                ),
                shape = RoundedCornerShape(26.dp)
            ) {
                if (viewModel.isLoading) {
                    CircularProgressIndicator(
                        modifier = Modifier.size(20.dp),
                        color = Color.White,
                        strokeWidth = 2.dp
                    )
                } else {
                    Text(
                        text = if (rechargeAmount.isNotEmpty()) "充值 ￥$rechargeAmount" else "请输入充值金额",
                        fontSize = 16.sp,
                        fontWeight = FontWeight.Bold,
                        fontFamily = CaiFont
                    )
                }
            }
            
            Spacer(modifier = Modifier.height(16.dp))
        }
    }
    
    // 支付宝二维码弹框
    if (showAlipayDialog && alipayQrcodeUrl != null) {
        println("RechargeScreen - 渲染支付宝二维码弹框")
        println("RechargeScreen - showAlipayDialog=$showAlipayDialog")
        println("RechargeScreen - alipayQrcodeUrl=$alipayQrcodeUrl")
        
        RechargeAlipayQrcodeDialog(
            qrcodeUrl = alipayQrcodeUrl!!,
            orderNo = alipayOrderNo ?: "",
            viewModel = viewModel,
            onDismiss = {
                showAlipayDialog = false
                alipayQrcodeUrl = null
                alipayOrderNo = null
            },
            onPaymentSuccess = {
                showAlipayDialog = false
                alipayQrcodeUrl = null
                alipayOrderNo = null
                // 显示成功提示
                viewModel.successMessage = "充值成功！"
            }
        )
    }
}

/**
 * 充值支付宝二维码弹框
 * 带支付状态检查功能
 */
@Composable
fun RechargeAlipayQrcodeDialog(
    qrcodeUrl: String,
    orderNo: String,
    viewModel: RechargeViewModel,
    onDismiss: () -> Unit,
    onPaymentSuccess: () -> Unit
) {
    println("RechargeAlipayQrcodeDialog - 显示支付宝二维码弹框: $qrcodeUrl")
    
    var isChecking by remember { mutableStateOf(false) }
    var checkErrorMessage by remember { mutableStateOf<String?>(null) }
    val coroutineScope = rememberCoroutineScope()
    val token = TokenManager.getToken()
    
    val scrollState = rememberScrollState()
    
    Dialog(onDismissRequest = onDismiss) {
        Card(
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp),
            shape = RoundedCornerShape(16.dp),
            colors = CardDefaults.cardColors(containerColor = Color.White)
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .heightIn(max = 600.dp)  // 限制最大高度
                    .verticalScroll(scrollState)  // 添加滚动支持
                    .padding(24.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                // 标题和关闭按钮
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        text = "支付宝扫码支付",
                        fontSize = 20.sp,
                        fontWeight = FontWeight.Bold,
                        fontFamily = CaiFont
                    )
                    IconButton(
                        onClick = onDismiss,
                        modifier = Modifier.size(24.dp)
                    ) {
                        Icon(
                            imageVector = Icons.Default.Close,
                            contentDescription = "关闭",
                            tint = Color.Gray
                        )
                    }
                }
                
                Spacer(modifier = Modifier.height(16.dp))
                
                Text(
                    text = "请使用支付宝扫描下方二维码完成支付",
                    fontSize = 14.sp,
                    color = Color.Gray,
                    fontFamily = CaiFont
                )
                
                Spacer(modifier = Modifier.height(16.dp))
                
                // 二维码图片
                Box(
                    modifier = Modifier
                        .size(250.dp)
                        .background(Color.White, RoundedCornerShape(12.dp))
                        .padding(16.dp),
                    contentAlignment = Alignment.Center
                ) {
                    QrcodeImageView(imageUrl = qrcodeUrl)
                }
                
                Spacer(modifier = Modifier.height(12.dp))
                
                // 显示图片地址（方便调试）
                Text(
                    text = "图片地址: $qrcodeUrl",
                    fontSize = 11.sp,
                    color = Color(0xFF999999),
                    fontFamily = CaiFont,
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 8.dp),
                    maxLines = 2,
                    lineHeight = 14.sp
                )
                
                Spacer(modifier = Modifier.height(12.dp))
                
                // 错误信息
                if (checkErrorMessage != null) {
                    Text(
                        text = checkErrorMessage!!,
                        fontSize = 12.sp,
                        color = Color.Red,
                        fontFamily = CaiFont,
                        modifier = Modifier.padding(vertical = 8.dp)
                    )
                }
                
                // 提示信息
                Text(
                    text = "支付完成后请点击下方按钮检查支付状态",
                    fontSize = 12.sp,
                    color = Color.Gray,
                    fontFamily = CaiFont,
                    modifier = Modifier.padding(top = 8.dp)
                )
                
                Spacer(modifier = Modifier.height(16.dp))
                
                // 检查支付状态按钮
                Button(
                    onClick = {
                        if (token.isNullOrEmpty()) {
                            checkErrorMessage = "未登录，无法检查支付状态"
                            return@Button
                        }
                        
                        isChecking = true
                        checkErrorMessage = null
                        
                        coroutineScope.launch {
                            try {
                                println("RechargeAlipayQrcodeDialog - 检查支付状态，订单号: $orderNo")
                                val checkResponse = viewModel.repository.getOrderDetail(orderNo, token)
                                
                                if (checkResponse.isSuccess) {
                                    val checkOrderDetail = checkResponse.getOrNull()
                                    if (checkOrderDetail?.code == 200 && checkOrderDetail.data != null) {
                                        val payStatus = checkOrderDetail.data.payStatus
                                        println("RechargeAlipayQrcodeDialog - 当前支付状态: $payStatus")
                                        
                                        if (payStatus == 1) {
                                            println("RechargeAlipayQrcodeDialog - 支付成功！")
                                            onPaymentSuccess()
                                        } else {
                                            checkErrorMessage = "请先完成支付，然后再检查状态"
                                        }
                                    } else {
                                        checkErrorMessage = "检查失败: ${checkOrderDetail?.message}"
                                    }
                                } else {
                                    checkErrorMessage = "网络请求失败，请稍后重试"
                                }
                            } catch (e: Exception) {
                                println("RechargeAlipayQrcodeDialog - 检查异常: ${e.message}")
                                checkErrorMessage = "检查异常: ${e.message}"
                            } finally {
                                isChecking = false
                            }
                        }
                    },
                    enabled = !isChecking,
                    modifier = Modifier.fillMaxWidth(),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = Color(0xFF4CAF50)
                    ),
                    shape = RoundedCornerShape(8.dp)
                ) {
                    if (isChecking) {
                        CircularProgressIndicator(
                            modifier = Modifier.size(20.dp),
                            color = Color.White,
                            strokeWidth = 2.dp
                        )
                        Spacer(modifier = Modifier.width(8.dp))
                        Text(
                            text = "检查中...",
                            fontFamily = CaiFont
                        )
                    } else {
                        Text(
                            text = "我已支付，检查状态",
                            fontFamily = CaiFont
                        )
                    }
                }
            }
        }
    }
}
